import type { TAuthConfig, TRefreshTokenExpiredEvent } from '@sangoma/react-oauth2-code-pkce';
import { v4 as uuidv4 } from 'uuid';
import { authRedirectService } from '../services/authRedirectService';
import { authStorageService } from '../services/authStorageService';
import { meetingSessionService } from '../services/meetingSessionService';
import { retrieveJitsiMeetId } from '../utils/jitsiAPIUtils';
import { resolveAppHostname } from './appOrigin';
import { usesProductionBackend } from './environmentHostnames';

// rspack may not inject import.meta.env at runtime
const env = import.meta.env as ImportMetaEnv | undefined;
const origin = window.location.origin;

/**
 * OAuth configuration for authentication services
 */

// biome-ignore format: looks better
const OAUTH_CONFIG = {
    prod: { serverUrl: 'https://auth.sangoma.com',         clientId: 'TyvuMVt3GV145aWGHghI', apiBaseUrl: 'https://cpaas.sangoma.com',        },
  appdev: { serverUrl: 'https://auth-appdev.sangoma.com',  clientId: '3a7QWJwG3E66C3HT0Ez4', apiBaseUrl: 'https://cpaas-appdev.sangoma.com', },
    test: { serverUrl: 'https://auth-devint.sangoma.com',  clientId: 'ikQCvOTm1eTzS6FMASdN', apiBaseUrl: 'https://cpaas-devint.sangoma.com', },
};

/**
 * @returns {string} Retrieves the OAuth config for a specific hostname
 */
export function getOAuthConfigForHostname(hostname: string) {
  if (usesProductionBackend(hostname)) {
    return OAUTH_CONFIG.prod;
  }

  if (typeof localStorage !== 'undefined') {
    const source = localStorage.getItem('sangoma-meet-oauth-config');
    if (source && source in OAUTH_CONFIG) {
      return OAUTH_CONFIG[source as keyof typeof OAUTH_CONFIG];
    }
  }

  return OAUTH_CONFIG.appdev;
}

/**
 * @returns {string} Retrieves the OAuth config for the current environment
 *
 * SM-1243: resolved from the origin the NG Meet bundle was served from. Using
 * `window.location.hostname` would report the TeamHub host in MF mode, miss
 * `PRODUCTION_HOSTNAMES`, and point production at appdev auth/CPaaS.
 */
function _getCurrentOAuthConfig() {
  const hostname = resolveAppHostname();
  if (hostname) {
    return getOAuthConfigForHostname(hostname);
  }

  return OAUTH_CONFIG.appdev;
}

const oAuthConfig = _getCurrentOAuthConfig();

/**
 * @returns {object} Returns the OAuth config for the current environment
 */
export function getCurrentOAuthConfig() {
  return oAuthConfig;
}

/**
 * @returns {object} Returns the CPAAS API base URL
 */
export function getCpaasApiBaseUrl() {
  return oAuthConfig.apiBaseUrl;
}

console.log(`Authenticating via ${oAuthConfig.serverUrl}`);

export const getAuthConfig = async (): Promise<TAuthConfig> => {
  const deviceId = await retrieveJitsiMeetId().catch(() => {
    const id = uuidv4().replace(/-/g, '');
    console.warn(`Failed to retrieve jitsiMeetId for auth, using random ID: ${id}`);
    return id;
  });

  return {
    clientId: env?.VITE_OAUTH_CLIENT_ID || oAuthConfig.clientId,
    authorizationEndpoint: env?.VITE_OAUTH_AUTH_URL || `${oAuthConfig.serverUrl}/oauth/authorize`,
    tokenEndpoint: env?.VITE_OAUTH_TOKEN_URL || `${oAuthConfig.serverUrl}/oauth/token`,
    logoutEndpoint: env?.VITE_OAUTH_LOGOUT_URL || `${oAuthConfig.serverUrl}/sso/logout`,
    redirectUri: origin,
    scope: 'openid starhub.integrations',
    state: 'dummy',
    loginMethod: 'redirect',
    // biome-ignore-start lint/style/useNamingConvention: OAuth protocol field names
    extraAuthParameters: {
      device_id: deviceId,
    },
    extraTokenHeaders: {
      'x-device-id': deviceId,
    },
    extraLogoutParameters: {
      redirect_uri: origin,
      id_token_hint: 'none',
      post_logout_redirect_uri: 'none',
      token: 'none',
      token_type_hint: 'none',
    },
    // biome-ignore-end lint/style/useNamingConvention: OAuth protocol field names
    // Fallback values when OAuth server doesn't provide expires_in fields.
    // Library priority: config.tokenExpiresIn ?? response.expires_in ?? config.defaultTokenExpiresIn ?? JWT.exp
    defaultTokenExpiresIn: 3600, // 1 hour
    defaultRefreshTokenExpiresIn: 2592000, // 30 days
    preLogin: () => {
      console.log('[Auth] Starting login...');
      authRedirectService.savePreLoginUrl();
    },
    postLogin: () => {
      console.log('[Auth] Login successful!');
      authStorageService.markAsLoggedIn();
      authStorageService.logTokenExpiry();
    },
    autoLogin: false,
    onRefreshTokenExpire: (event: TRefreshTokenExpiredEvent) => {
      const doLogin = () => event.logIn(undefined, undefined, 'redirect');

      if (meetingSessionService.deferLoginIfMeetingActive(doLogin)) {
        authStorageService.clearRefreshToken();
        return;
      }

      console.log('[Auth] Refresh token expired - re-authentication required');
      doLogin();
    },
  };
};
